import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import { type MouseEvent, type ReactNode, useCallback } from "react";
import { cx } from "../../lib/cx";

/**
 * Tabs on Base UI's Tabs, drawn as A2's pill tablist (the plan's A/B/C).
 *
 * A2 hand-rolled `role="tablist"` with every tab a tab stop and no arrow keys
 * (`plan.tsx:78-93`). Base UI gives one tab stop, ←/→ (Home/End) to move,
 * and the tab↔panel wiring. `activateOnFocus` is on: the panels are already
 * rendered data, so moving is choosing, as in a segmented control.
 *
 * Controlled (plan D09). The parts are separate so a tab can hold more than
 * a word — the plan's tab is a letter, a weekday and a "today" dot.
 */

export interface TabsProps<T extends string> {
  children: ReactNode;
  className?: string;
  onValueChange: (value: T) => void;
  value: T;
}

export function Tabs<T extends string>({
  value,
  onValueChange,
  className,
  children,
}: TabsProps<T>) {
  // Every value the root reports is one of our tabs' values.
  const change = useCallback(
    (next: unknown) => {
      onValueChange(next as T);
    },
    [onValueChange]
  );
  return (
    <BaseTabs.Root className={className} onValueChange={change} value={value}>
      {children}
    </BaseTabs.Root>
  );
}

export interface TabListProps {
  children: ReactNode;
  className?: string;
  /** The list's accessible name. */
  label: string;
}

export function TabList({ label, className, children }: TabListProps) {
  return (
    <BaseTabs.List
      activateOnFocus
      aria-label={label}
      className={cx(
        "grid auto-cols-fr grid-flow-col gap-1 rounded-full bg-surface-2 p-1",
        className
      )}
    >
      {children}
    </BaseTabs.List>
  );
}

export interface TabProps {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  /**
   * A long press on a phone (Android's WebView fires `contextmenu` for it)
   * or a right click — the plan's day tabs open "Organizar treinos" with it
   * (`D215`). The caller prevents the browser's own menu.
   */
  onContextMenu?: (event: MouseEvent<HTMLElement>) => void;
  value: string;
}

export function Tab({
  value,
  disabled,
  className,
  children,
  onContextMenu,
}: TabProps) {
  return (
    <BaseTabs.Tab
      className={cx(
        "relative flex h-12 min-w-0 cursor-pointer select-none items-center justify-center gap-2 rounded-full px-3 font-medium text-base text-ink-2 transition-[background-color,box-shadow] hover:bg-wash data-disabled:cursor-not-allowed data-active:bg-thumb data-active:font-semibold data-active:text-ink data-disabled:opacity-40 data-active:shadow",
        className
      )}
      disabled={disabled}
      onContextMenu={onContextMenu}
      value={value}
    >
      {children}
    </BaseTabs.Tab>
  );
}

export interface TabPanelProps {
  children: ReactNode;
  className?: string;
  value: string;
}

export function TabPanel({ value, className, children }: TabPanelProps) {
  return (
    <BaseTabs.Panel className={className} value={value}>
      {children}
    </BaseTabs.Panel>
  );
}
